/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import { RoleFunction } from '@eagle/common';
import { Dashboard } from '@eagle/core-data-types';
import {
  FindItemsDeferredResult,
  getListResultDescription,
  Icon,
  ListPage,
  ListPageQuery,
  Pagination,
  ResultsDetailCard,
  SearchResultsTableView,
  Undefinable,
  useAuthenticated,
  useBoolFlag,
  useCustomRoutes,
  useHasAuthorization
} from '@eagle/react-common';
import { Button, Card, Stack, styled, TableBody, TableCell, TableRow, Typography } from '@ethings/ui-toolkit';
import { TableRow as MUITableRow } from '@mui/material';
import Axios from 'axios';
import { FC, useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHref, useNavigate } from 'react-router-dom';
import { DASHBOARD_DETAIL_FLAG } from '../dashboard-detail/dashboard-detail';

interface StyledTableBodyProps {
  isLoading: boolean;
}

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledTableBodyProps>(({ isLoading }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
}));

const StyledTableRow = styled(TableRow)() as typeof MUITableRow;

export const DashboardsList: FC = () => {
  const { restClient } = useAuthenticated();
  const { t } = useTranslation(['common', 'track']);
  const { dashboards } = useCustomRoutes();
  const navigate = useNavigate();
  const href = useHref(`/${dashboards}`);

  const { hasAuthorization } = useHasAuthorization();
  const SYSTEM_ADMINISTRATOR_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR] as const;
  const hasManagePermissions = hasAuthorization(SYSTEM_ADMINISTRATOR_ROLE) && useBoolFlag(DASHBOARD_DETAIL_FLAG);

  const navigateToDashboards = (item: Dashboard): void => {
    navigate(`/${dashboards}/${encodeURIComponent(item._id)}`);
  };

  const findDashboards = useCallback(({ pagination }: ListPageQuery): FindItemsDeferredResult<Dashboard> => {
    const cancelToken = Axios.CancelToken.source();

    return {
      cancel: () => cancelToken.cancel(),
      promise: restClient.my.dashboards.getAllV2({
        ...pagination,
        sort: JSON.stringify({ display: 'asc' }),
        filter: { type: 'dashboard' },
      }, { cancelToken: cancelToken.token }).then((response) => {
        const matchCount = response.count ?? 0;
        const resultDescription = getListResultDescription({ count: matchCount, entityKey: 'common:terms.dashboard', t });

        return {
          result: {
            results: response.items,
            itemCount: matchCount,
          },
          resultDescription,
        };
      }),
    };
  }, [restClient]);

  const renderDesktopContent = (isLoading: boolean, items: Dashboard[]): JSX.Element => (
    <StyledTableBody isLoading={isLoading} component="div">
      {items.map((item, i) => (
        <StyledTableRow
          key={i}
          component={Link}
          hover
          to={`/${dashboards}/${item._id}`}
          sx={{ cursor: 'pointer', height: 60, textDecoration: 'none' }}
        >
          <TableCell component="div">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography>{item.display}</Typography>
              {hasManagePermissions
                && <Button
                  onClick={(e) => {
                    navigate(`/${dashboards}/manage/${item._id}`);
                    e.preventDefault();
                  }}
                  variant="text"
                >
                  {t('track:page.dashboards.manage.action')}
                </Button>
              }
            </Stack>
          </TableCell>
        </StyledTableRow>
      ))}
    </StyledTableBody>
  );

  const renderMobileContent = (_displayOnCard: boolean, isLoading: boolean, items: Dashboard[]): JSX.Element => (
    <Stack spacing={1} width={1}>
      {items.map((item, i) => (
        <Card key={i} style={{ filter: isLoading ? 'blur(1px)' : '', overflow: 'visible', width: '100%' }}>
          <ResultsDetailCard
            content={<Typography>{item.display}</Typography>}
            href={`${href}/${item._id}`}
            sx={{ py: 1, px: 2 }}
          />
        </Card>
      ))}
    </Stack>
  );

  const renderTableContent = (
    isLoading: boolean,
    items: Undefinable<Dashboard[]>,
    matchCount: number,
    pagination: Pagination,
    setPagination: (value: Pagination) => void,
    text: Undefinable<string>,
    error?: Error,
  ): JSX.Element => (
    <SearchResultsTableView<Dashboard>
      data-testid='dashboard-list-results'
      error={error}
      isLoading={isLoading}
      items={items}
      matchCount={matchCount}
      noResultsInstructions={t('track:page.dashboards-list.no-dashboards.hint')}
      pagination={pagination}
      renderDesktopContent={renderDesktopContent}
      renderMobileContent={renderMobileContent}
      setPagination={setPagination}
      text={text}
    />
  );

  return (
    <ListPage<Dashboard>
      id='dashboard'
      data-testid='dashboards-list'
      actions={[]}
      filterFields={[]}
      icon={<Icon icon="DashboardIcon" />}
      navigateToEntity={navigateToDashboards}
      onQueryChanged={findDashboards}
      renderTableContent={renderTableContent}
      searchRemoved
      showFilterButton={false}
      storageKey="dashboards"
      title={t('track:page.dashboards.title')}
    />
  );
};
